import Actions from '../../actions/actions';
import GenerateDocuments from '../documents/generate_documents';
import EventDrivenDispatchers from '../event_driven_dispatchers';
import { isDebugEnabled } from '../../helpers/debug_helper';
import Constants from '../../helpers/constants';
import FinancingHelper from '../../helpers/financing_helper';
import GenericProductText from '../../helpers/generic_language';

const SavingsCalculator = 'savingsCalculator';
const CollectContactInformation = 'collectAddressInformation';
const EligibilityPage = 'eligibilityStatus';
const StepDocuments = 'stepDocuments';
const QuickCheckStatus = 'quickCheckStatus';

class WizardSteps {
  constructor({
    isGuestUser,
    billBeforeAgree,
    proposal,
    wizardStepsDebug,
    userFunctionalityAcls,
    customer,
    financingMethod,
  }) {
    this.isGuestUser = isGuestUser;
    this.billBeforeAgree = billBeforeAgree;
    this.proposal = proposal;
    this.wizardStepsDebug = wizardStepsDebug;
    this.financingMethod = financingMethod;
    const quickCheckEnabled = userFunctionalityAcls?.some(
      (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_FEATURE_CODE_QUICK_CHECK
    );
    let privateSteps = [
      {
        pageId: QuickCheckStatus,
        icon: 'bolt',
        label: 'QuickCheck',
        onLoad: async (oldStepNum, props) => Actions.goToProposalPage({ subPage: 'quickCheckStatus' }),
        onLeave: async (newStepNum, props) => 'ok',
        isNavigable: () => true, // important
        isClickable: () => true, // important
        isComplete: () => true,
      },
      {
        pageId: SavingsCalculator,
        icon: 'dollar',
        label: 'Proposal',
        onLoad: () => Actions.goToProposalPage({ subPage: 'customize' }),
        isNavigable: () => true, // important
        isClickable: () => true, // important
        isComplete: (props) =>
          !!props.eligibility ||
          typeof props.userInputLoanAmountIsInvalid !== 'boolean' ||
          !props.userInputLoanAmountIsInvalid,
        // important, technically if its not a boolean, it never changed, and we should be able to trust the backend
      },
      {
        pageId: CollectContactInformation,
        icon: 'person',
        label: 'Contact Info',
        onLoad: () => {
          Actions.toggleCoApplicantForm(true);
          Actions.goToProposalPage({ subPage: 'address' });
        },
        onLeave: async (newStepNum, props) => {
          if (props.proposal.financingRequestId) {
            return 'ok';
          }

          let movingBackward = newStepNum < this.stepIndexById(CollectContactInformation);
          let moving1Forward = newStepNum === this.stepIndexById(CollectContactInformation) + 1;

          let addressMethod;
          if (movingBackward) {
            addressMethod = 'submitCustomerData.skipvalidation';
          } else if (moving1Forward) {
            addressMethod = 'submitCustomerData';
          }

          if (addressMethod) {
            return await Actions.triggerAddressInfo({ addressInfoMethod: addressMethod });
          }
          return 'ok';
        },
        isComplete: (props) => {
          return !!props.customerFormValid || !!props.eligibility;
        },
      },
      {
        pageId: EligibilityPage,
        icon: 'checkedBox',
        label: GenericProductText.getProductText({
          text_sku: 'breadcrumb_eligibility',
          financingMethod,
        }),
        hasBreadcrumb: (props) => !!props?.eligibility,
        nextButtonCls: (props) => {
          const { eligibility, financingMethod, userInputLoanAmountIsInvalid } = props;

          const canContinue =
            !userInputLoanAmountIsInvalid &&
            GenerateDocuments.canViewDocs(eligibility, financingMethod, props.customer) &&
            (this.getStep(EligibilityPage).isComplete(props) ||
              GenerateDocuments.canGenerateDocs(eligibility, financingMethod, customer));
          return canContinue ? '' : 'hidden';
        },
        onLoad: (oldStepNum, props) => {
          Actions.goToProposalPage({
            subPage: 'eligibilityStatus',
            proposalId: props.proposal.id,
          });
        },
        onLeave: async (newStepNum, props) => {
          if (!props) {
            return false;
          }
          const { financingMethod, eligibility, updateFinancingMethodLocked, customer } = props;
          let nextStepIsAgree = newStepNum === this.stepIndexById(EligibilityPage) + 1;

          if (updateFinancingMethodLocked || (nextStepIsAgree && !eligibility)) {
            return;
          }

          if (nextStepIsAgree && !this.getStep(EligibilityPage).isComplete(props)) {
            if (!GenerateDocuments.canGenerateDocs(eligibility, financingMethod, customer)) {
              Actions.setErrorMessages(
                customer?.status === 'Cancelled'
                  ? ['Cancelled Customers cannot generate documents']
                  : ['Cannot generate documents']
              );
              return false;
            }

            await GenerateDocuments.generateDocs(proposal.id, financingMethod.id, customer);
          }
          return 'ok';
        },
        isComplete: (props) => {
          if (!props) {
            return false;
          }
          const { eligibility, proposal, financingRequest, financingMethod, customer, userInputLoanAmountIsInvalid } =
            props;
          return (
            (typeof userInputLoanAmountIsInvalid !== 'boolean' || !userInputLoanAmountIsInvalid) &&
            GenerateDocuments.canViewDocs(eligibility, financingMethod, customer) &&
            ((proposal && !!proposal.financingRequestId) || financingRequest)
          );
        },
      },
      {
        pageId: StepDocuments,
        icon: 'document',
        label: 'Documents',
        onLoad: (oldStepNum, props) => {
          Actions.goToProposalPage({
            subPage: 'step-documents',
            financingRequestId: props.proposal.financingRequestId,
          });
        },
        onLeave: async (newStepNum, props) => {
          if (this.getStep(StepDocuments).isComplete(props)) {
            return;
          }
          if (this.getStep(StepDocuments).isBefore(newStepNum)) {
            try {
              // generate documents irdocs
              await EventDrivenDispatchers.emitAndWait(
                EventDrivenDispatchers.types.UserSignaturesComplete,
                'ok',
                EventDrivenDispatchers.types.SignatureIRDocsGenerated
              );
            } catch (e) {
              Actions.handleRequestError({ error: e.details, defaultErrorMessage: e.details.message });
              throw e;
            }
          }
        },
        isComplete: () => {
          return false;
        },
      },
    ];

    // Filter & put steps in order
    let orderedStepNames = [
      // if customer has prescreen or the user goes directly to the status page (if user goes directly to status page the hasPrescreen flag may not be returned 'yet'
      quickCheckEnabled
        ? customer?.hasPrescreen || window.location.href.includes('quickCheckStatus')
          ? QuickCheckStatus
          : null
        : null,
      FinancingHelper.isCashProduct(financingMethod) ? undefined : SavingsCalculator,
      CollectContactInformation,
      EligibilityPage,
      StepDocuments, // agree terms
    ].filter((e) => e);

    this.STEPS_IN_ORDER = orderedStepNames.map((name) =>
      Object.assign(
        {},
        privateSteps.find(({ pageId }) => pageId === name)
      )
    ); // find and rough copy
  }

  stepIndexById(pageIdOrNumber) {
    if (typeof pageIdOrNumber === 'string') {
      return this.STEPS_IN_ORDER.findIndex((step) => step.pageId === pageIdOrNumber);
    }
    if (typeof pageIdOrNumber === 'number') {
      return pageIdOrNumber;
    }
    return null;
  }

  stepIsBefore(pageId1, pageId2) {
    return this.stepIndexById(pageId1) < this.stepIndexById(pageId2);
  }

  stepIsAfter(pageId1, pageId2) {
    return this.stepIndexById(pageId1) < this.stepIndexById(pageId2);
  }

  checkStep(currentStep, props, stepNum) {
    return this.stepsBeforeAreComplete(props, stepNum) || this.isTheStepBefore(currentStep, stepNum);
  }

  getStep(stepNum) {
    if (typeof stepNum === 'string') {
      stepNum = this.stepIndexById(stepNum);
    }

    let theStep = this.STEPS_IN_ORDER[stepNum];

    if (!theStep) {
      return null;
    }

    let defaultMethods = {
      onLoad: () => {},
      onLeave: () => {},

      isNavigable: (currentStep, props) => this.stepsBeforeAreComplete(props, stepNum),
      isClickable: (currentStep, props) => this.checkStep(currentStep, props, stepNum),
      isAfter: (otherStep) => this.stepIsAfter(stepNum, otherStep),
      isBefore: (otherStep) => this.stepIsBefore(stepNum, otherStep),
      isComplete: () => true,
      hasBreadcrumb: () => true,
      after: async () => {},

      isProcessingLocksStep: () => false,
      stepsNavigation: () => true,
      nextButtonCls: () => '',
    };
    let result = Object.assign(defaultMethods, theStep);

    if (!isDebugEnabled()) {
      return result;
    }

    return Object.keys(result).reduce((acc, key) => {
      const self = this;
      if (typeof result[key] === 'function') {
        acc[key] = function () {
          let res = result[key].apply(self, arguments);
          // Logger.debug(`${theStep.pageId}.${key}() returned ${res}`);
          return res;
        };
      } else {
        acc[key] = result[key];
      }
      return acc;
    }, {});
  }

  stepsBefore(stepNum) {
    if (typeof stepNum === 'string') {
      stepNum = this.stepIndexById(stepNum);
    }

    return this.STEPS_IN_ORDER.filter((step, index) => index < stepNum).map((step, stepIndex) =>
      this.getStep(stepIndex)
    ); // make sure we get all the functions
  }

  async stepsBeforeRunAfter(stepNum, props) {
    return this.stepsBefore(stepNum).reduce((acc, step) => {
      // aaaah promising chaining!
      return acc.then(step.after(props));
    }, Promise.resolve());
  }

  stepsBeforeAreComplete(props, stepNum) {
    return this.stepsBefore(stepNum).every((step) => step.isComplete(props));
  }

  getSteps() {
    return this.STEPS_IN_ORDER;
  }

  isTheStepBefore(step1, step2) {
    if (typeof step1 === 'string') {
      step1 = this.stepIndexById(step1);
    }
    if (typeof step2 === 'string') {
      step2 = this.stepIndexById(step2);
    }

    return step1 + 1 === step2;
  }

  getLastIncomplete(props) {
    if (
      this.wizardStepsDebug &&
      typeof this.wizardStepsDebug === 'object' &&
      typeof this.wizardStepsDebug.getLastIncomplete !== 'undefined'
    ) {
      // kinda gross but needed for a unit test now that it isnt static
      return this.wizardStepsDebug.getLastIncomplete;
    }

    return this.getStep(this.STEPS_IN_ORDER.findIndex((nope, index) => !this.getStep(index).isComplete(props)));
  }

  getLastStep() {
    return this.getStep(this.STEPS_IN_ORDER.length - 1);
  }
}

export { WizardSteps };
